import {
    type FC,
    useState,
    useRef,
    useCallback,
    useEffect,
    useMemo,
    useContext,
    createContext,
    type RefCallback,
    type CSSProperties,
    type ReactNode,
} from 'react';
import classnames from 'classnames';
import { RotatingArrow } from 'dibs-elements/exports/RotatingArrow.tsx';
import { Link } from 'dibs-elements/exports/Link.tsx';

import styles from './ResponsiveExpandingContent.scss';

/* 
  Provider, Trigger, and useResponsiveExpandingContent are decoupled to allow 
  for layout flexibility. All three components must be used for 
  ResponsiveExpandingContent to work. 
*/

const Ctx = createContext<[expanded: boolean, setExpanded: () => void, expandable: boolean]>([
    false,
    () => {},
    false,
]);

export const ResponsiveExpandingContentProvider: FC<{
    deviceType?: string;
    breakpoints?: Record<'mobile' | 'tablet' | 'desktop', boolean>;
    children: ReactNode;
}> = ({
    children,
    deviceType, // required for responsive functionality (breakpoints)
    breakpoints: { mobile = true, tablet = false, desktop = false } = {
        mobile: true,
        tablet: false,
        desktop: false,
    }, // enable/disable expanding styles/behavior at a given breakpoint
}) => {
    const [expanded, setExpanded] = useState(false);
    const expandable = useMemo(() => {
        const responsiveBreakpoints: Record<string, boolean> = { mobile, tablet, desktop };
        return responsiveBreakpoints[deviceType || ''] ?? true;
    }, [desktop, deviceType, mobile, tablet]);
    const toggle = useCallback(() => setExpanded(prev => !prev), []);
    return <Ctx.Provider value={[expanded, toggle, expandable]}>{children}</Ctx.Provider>;
};

export const ResponsiveExpandingContentTrigger: FC<{ children: ReactNode }> = ({ children }) => {
    const [expanded, toggle, isExpandable] = useContext(Ctx);
    // if ExpandingContent is !isExpandable, do not add button and arrow
    if (!isExpandable) {
        return <>{children}</>;
    }
    return (
        <Link
            ariaExpanded={!!expanded}
            onClick={toggle}
            buyerLinkType="linkNoDecoration"
            className={classnames(styles.trigger, styles.important)}
        >
            {children}
            <RotatingArrow size="small" direction={expanded ? 'up' : 'down'} />
        </Link>
    );
};

// measure height of contents instead of element clientHeight. this method
// allows the element to initially render with height: 0.
function getHeight(element: HTMLElement): string {
    const range = document.createRange();
    range.selectNodeContents(element);
    return range.getBoundingClientRect().height + 'px';
}

// returns props and ref to use with the element you wish to make expandable.
// applies expanding styles and behavior for breakpoints specified in parameters.
export function useResponsiveExpandingContent(): [
    ref: RefCallback<HTMLElement>,
    props: { style: CSSProperties; className: string },
] {
    const [expanded, , expandable] = useContext(Ctx);
    // initialize height - null prevents custom properties from working and disables
    // collapsing style.
    const [height, setHeight] = useState<string | null>(expandable ? '0px' : null);
    const ref = useRef<HTMLElement | null>(null);
    const timeoutRef = useRef<number>(0);
    // create props for content element
    const props = useMemo(
        () => ({
            style: { '--height': height, ['--expanded']: expanded ? 1 : 0 } as CSSProperties,
            className: classnames(styles.important, { [styles.expandable]: expandable }),
        }),
        [height, expanded, expandable]
    );

    // disable height if !expandable
    useEffect(() => {
        if (!expandable) {
            setHeight(null);
        }
    }, [expandable]);

    // reset height on resize
    const resizeListener = useCallback(() => {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
            if (ref.current) {
                setHeight(expandable ? getHeight(ref.current) : null);
            }
            // allow execution @ 15fps
        }, 1000 / 15) as unknown as number;
    }, [expandable]);

    // listen for window resize events
    useEffect(() => {
        window.addEventListener('resize', resizeListener, { passive: true });
        return () => window.removeEventListener('resize', resizeListener);
    }, [resizeListener]);

    const refCallback: RefCallback<HTMLElement> = useCallback(
        element => {
            // setHeight if element exists and != ref
            if (element && !ref.current) {
                if (expandable) {
                    setHeight(getHeight(element));
                }
            }
            // update ref
            ref.current = element;
        },
        [expandable]
    );

    return [refCallback, props];
}
